/** ContentPanel
 * The purpose of this component is to do the following:
 * -- Return groups of horizontal content rows
 * -- Retrieve card data for each row
 * -- Control load order / ensure rows are rendered in correct sequence
 */
import React, { useEffect, useState } from "react";

import { LoadingSpinner } from "@newsela/angelou";
import { isNil, isUndefined } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import RowsGenerator from "@/browse/RowsGenerator";
import {
  contentPanelWrapperLoadingCss,
  contentPanelRowWrapperCss,
} from "./ContentPanel.style";

const propTypes = {
  /**
   * Object used to render Pages. Derived from content API v3 response.
   */
  content: PropTypes.object,
  /**
   * Array used to render Pages. Derived from content API v3 response.
   */
  members: PropTypes.array,
  /**
   * Callback function for intersection observer
   */
  onIntersect: PropTypes.func,
};

const ContentPanel = (props) => {
  const { content, members, onIntersect } = props;
  const [count, setCount] = useState(0);

  // This instance of useEffect helps us control render order of our rows
  useEffect(() => {
    if (isUndefined(members)) return;

    let counter = count;

    const interval = setInterval(
      () => {
        if (counter >= members.length) {
          clearInterval(interval);
        } else {
          setCount((count) => count + 1);
          counter++;
        }
      },
      count > 5 ? 10 : 100,
    );

    return () => clearInterval(interval);
  }, [content]);

  const generateRows = () => {
    if (isNil(members)) return;

    // This helps control the candence of how rows render
    // Without this the first row wouldn't ncessesarily load first
    // So, we create a hash table of rows
    // When we create a hashTable we need not worry about duplicateive entries and order is preserved
    const rowHashTable = Object.fromEntries(
      // What we are doing here is to slow down the creation of rows enough so they load sequentially
      // We do this by slicing the members array from 0 - count, which is increasing incrementally
      members?.slice(0, count).map((member, index) => {
        return [
          member.content_id,
          <RowsGenerator
            contentId={member.content_id}
            key={`${member.content_id}-content-panel`}
            onIntersect={onIntersect}
            rowIndex={index}
            /** This will be used to evaluate which row type will be rendered based
             * on the title, specifically for CE Video and CE Content Rows */
            rowTitle={member.title}
            /** This will be used to evaluate if a Row links to a multi topic or
             * single topic page */
            topicMembers={member.members}
          />,
        ];
      }),
    );

    const hashValues = Object?.values(rowHashTable);

    if (isNil(hashValues)) {
      return (
        <div css={contentPanelWrapperLoadingCss}>
          <LoadingSpinner />
        </div>
      );
    }

    return <div css={contentPanelRowWrapperCss}>{hashValues}</div>;
  };

  return generateRows();
};

ContentPanel.propTypes = propTypes;
export default React.memo(ContentPanel);
